import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import "./FixedColumnTable.css";
import MonthSelectorDropdown from "../../MonthSelectorDropdown";
import * as XLSX from "xlsx";
import { useSalesContext } from "../../SalesContext";
import ArrowUpwardIcon from "../../../assets/images/ArrowUpwardIcon_green.svg";
import ArrowDownwardIcon from "../../../assets/images/ArrowDownwardIcon_red.svg";

interface MonthData {
  LYTD: string | number;
  CYTD: string | number;
  Growth: number;
  Target: string | number;
  Per_Ach: string | number;
  [key: string]: any;
}

interface QuarterData {
  LYTD: number;
  CYTD: number;
  Growth: number;
  Target: number;
  Per_Ach: string | number;
  [key: string]: any;
}

interface GrandTotal {
  LYTD: number;
  CYTD: number;
  Target: number;
  Per_Ach: string | number;
  Growth: number;
  [key: string]: any;
}

interface HQ {
  Name: string;
  Months: { [key: string]: MonthData };
  Quarters: { [key: string]: QuarterData };
  GrandTotal: GrandTotal;
  Balance: number;
}

interface Region {
  Name: string;
  HQs: HQ[];
  Region_Months: { [key: string]: MonthData };
  Region_Quarters: { [key: string]: QuarterData };
  Region_GrandTotal: GrandTotal;
  Region_Balance: number;
}

interface TableRowData {
  Zone: string;
  Regions: Region[];
  Zone_Months: { [key: string]: MonthData };
  Zone_Quarters: { [key: string]: QuarterData };
  Zone_GrandTotal: GrandTotal;
  Zone_Balance: number;
}

const zeroedMonthData: MonthData = {
  LYTD: 0,
  CYTD: 0,
  Growth: 0,
  Target: 0,
  Per_Ach: 0,
};
const zeroedQuarterData: QuarterData = {
  LYTD: 0,
  CYTD: 0,
  Growth: 0,
  Target: 0,
  Per_Ach: 0,
};
const defaultGrandTotal: GrandTotal = {
  LYTD: 0,
  CYTD: 0,
  Growth: 0,
  Target: 0,
  Per_Ach: 0,
};

const adjustValueByUnit = (
  value: string | number,
  salesUnit: string
): number => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (salesUnit === "Lacs") {
    return Number((numValue / 100000).toFixed(2));
  } else if (salesUnit === "Crores") {
    return Number((numValue / 10000000).toFixed(2));
  }
  return Number(numValue.toFixed(2));
};

const ensureCompleteHQData = (hq: Partial<HQ>, salesUnit: string): HQ => {
  const allMonths = [
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "January",
    "February",
    "March",
  ];

  const allQuarters = ["Q1", "Q2", "Q3", "Q4"];
  const hasMonthData = hq.Months && Object.keys(hq.Months).length > 0;
  const monthDefault = hasMonthData
    ? zeroedMonthData
    : { LYTD: 0, CYTD: 0, Growth: 0, Target: 0, Per_Ach: 0 };
  const completeMonths = allMonths.reduce((acc, month) => {
    const monthData = hq.Months?.[month];
    return {
      ...acc,
      [month]: monthData
        ? {
            LYTD: adjustValueByUnit(monthData.LYTD, salesUnit),
            CYTD: adjustValueByUnit(monthData.CYTD, salesUnit),
            Growth: monthData.Growth,
            Target: adjustValueByUnit(monthData.Target, salesUnit),
            Per_Ach: monthData.Per_Ach,
          }
        : monthDefault,
    };
  }, {});
  const hasQuarterData = hq.Quarters && Object.keys(hq.Quarters).length > 0;
  const quarterDefault = hasQuarterData
    ? zeroedQuarterData
    : { LYTD: 0, CYTD: 0, Growth: 0, Target: 0, Per_Ach: "" };
  const completeQuarters = allQuarters.reduce((acc, quarter) => {
    const quarterData = hq.Quarters?.[quarter];
    return {
      ...acc,
      [quarter]: quarterData
        ? {
            LYTD: adjustValueByUnit(quarterData.LYTD, salesUnit),
            CYTD: adjustValueByUnit(quarterData.CYTD, salesUnit),
            Growth: quarterData.Growth,
            Target: adjustValueByUnit(quarterData.Target, salesUnit),
            Per_Ach: quarterData.Per_Ach,
          }
        : quarterDefault,
    };
  }, {});
  const grandTotalData = hq.GrandTotal || defaultGrandTotal;
  return {
    Name: hq.Name || "Unnamed HQ",
    Months: completeMonths,
    Quarters: completeQuarters,
    GrandTotal: {
      LYTD: adjustValueByUnit(grandTotalData.LYTD, salesUnit),
      CYTD: adjustValueByUnit(grandTotalData.CYTD, salesUnit),
      Growth: grandTotalData.Growth,
      Target: adjustValueByUnit(grandTotalData.Target, salesUnit),
      Per_Ach: grandTotalData.Per_Ach,
    },
    Balance: adjustValueByUnit(hq.Balance || 0, salesUnit),
  };
};

const ensureCompleteRegionData = (
  region: Partial<Region>,
  salesUnit: string
): Region => {
  const allMonths = [
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "January",
    "February",
    "March",
  ];

  const allQuarters = ["Q1", "Q2", "Q3", "Q4"];
  const hasMonthData =
    region.Region_Months && Object.keys(region.Region_Months).length > 0;
  const monthDefault = hasMonthData
    ? zeroedMonthData
    : { LYTD: 0, CYTD: 0, Growth: 0, Target: 0, Per_Ach: 0 };
  const completeMonths = allMonths.reduce((acc, month) => {
    const monthData = region.Region_Months?.[month];
    return {
      ...acc,
      [month]: monthData
        ? {
            LYTD: adjustValueByUnit(monthData.LYTD, salesUnit),
            CYTD: adjustValueByUnit(monthData.CYTD, salesUnit),
            Growth: monthData.Growth,
            Target: adjustValueByUnit(monthData.Target, salesUnit),
            Per_Ach: monthData.Per_Ach,
          }
        : monthDefault,
    };
  }, {});
  const hasQuarterData =
    region.Region_Quarters && Object.keys(region.Region_Quarters).length > 0;
  const quarterDefault = hasQuarterData
    ? zeroedQuarterData
    : { LYTD: 0, CYTD: 0, Growth: 0, Target: 0, Per_Ach: 0 };
  const completeQuarters = allQuarters.reduce((acc, quarter) => {
    const quarterData = region.Region_Quarters?.[quarter];
    return {
      ...acc,
      [quarter]: quarterData
        ? {
            LYTD: adjustValueByUnit(quarterData.LYTD, salesUnit),
            CYTD: adjustValueByUnit(quarterData.CYTD, salesUnit),
            Growth: quarterData.Growth,
            Target: adjustValueByUnit(quarterData.Target, salesUnit),
            Per_Ach: quarterData.Per_Ach,
          }
        : quarterDefault,
    };
  }, {});
  const grandTotalData = region.Region_GrandTotal || defaultGrandTotal;
  return {
    Name: region.Name || "Unnamed Region",
    HQs: region.HQs?.map((hq) => ensureCompleteHQData(hq, salesUnit)) || [],
    Region_Months: completeMonths,
    Region_Quarters: completeQuarters,
    Region_GrandTotal: {
      LYTD: adjustValueByUnit(grandTotalData.LYTD, salesUnit),
      CYTD: adjustValueByUnit(grandTotalData.CYTD, salesUnit),
      Growth: grandTotalData.Growth,
      Target: adjustValueByUnit(grandTotalData.Target, salesUnit),
      Per_Ach: grandTotalData.Per_Ach,
    },
    Region_Balance: adjustValueByUnit(region.Region_Balance || 0, salesUnit),
  };
};

const FixedColumnTable: React.FC = () => {
  const { kpiData, salesUnit, loading } = useSalesContext();
  const [title, setTitle] = useState("Monthly Performance");
  const [showZoneData, setShowZoneData] = useState<boolean>(false);
  const allMonths = [
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "January",
    "February",
    "March",
  ];
  const [selectedMonths, setSelectedMonths] = useState<string[]>(allMonths);
  const [showRegion, setShowRegion] = useState<boolean>(true);
  const [showHQ, setShowHQ] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<"month" | "quarter">("month");
  const [expandedZones, setExpandedZones] = useState<string[]>([]);
  const [expandedRegions, setExpandedRegions] = useState<string[]>([]);

  const card3Data = kpiData?.kpi_data?.card3_data;

  const zeroedMonthData = {
    LYTD: 0,
    CYTD: 0,
    Growth: 0,
    Target: 0,
    Per_Ach: 0,
  };

  const Quarters = ["Q1", "Q2", "Q3", "Q4"];

  const data: TableRowData[] = React.useMemo(() => {
    if (!card3Data || !salesUnit) {
      console.warn("card3Data or salesUnit is undefined");
      return [];
    }

    return card3Data.map((row: TableRowData) => {
      const completeZoneMonths = allMonths.reduce((acc, month) => {
        const monthData = row.Zone_Months?.[month];
        return {
          ...acc,
          [month]: monthData
            ? {
                LYTD: adjustValueByUnit(monthData.LYTD, salesUnit),
                CYTD: adjustValueByUnit(monthData.CYTD, salesUnit),
                Growth: monthData.Growth,
                Target: adjustValueByUnit(monthData.Target, salesUnit),
                Per_Ach: monthData.Per_Ach,
              }
            : zeroedMonthData,
        };
      }, {});

      const completeZoneQuarters = Quarters.reduce((acc, quarter) => {
        const quarterData = row.Zone_Quarters?.[quarter];
        return {
          ...acc,
          [quarter]: quarterData
            ? {
                LYTD: adjustValueByUnit(quarterData.LYTD, salesUnit),
                CYTD: adjustValueByUnit(quarterData.CYTD, salesUnit),
                Growth: quarterData.Growth,
                Target: adjustValueByUnit(quarterData.Target, salesUnit),
                Per_Ach: quarterData.Per_Ach,
              }
            : zeroedMonthData,
        };
      }, {});

      const zoneGrandTotal = row.Zone_GrandTotal
        ? {
            LYTD: adjustValueByUnit(row.Zone_GrandTotal.LYTD, salesUnit),
            CYTD: adjustValueByUnit(row.Zone_GrandTotal.CYTD, salesUnit),
            Growth: row.Zone_GrandTotal.Growth,
            Target: adjustValueByUnit(row.Zone_GrandTotal.Target, salesUnit),
            Per_Ach: row.Zone_GrandTotal.Per_Ach,
          }
        : zeroedMonthData;

      return {
        ...row,
        Zone_Months: completeZoneMonths,
        Zone_Quarters: completeZoneQuarters,
        Zone_GrandTotal: zoneGrandTotal,
        Zone_Balance: row.Zone_Balance ?? 0,
        Regions: row.Regions.map((region) =>
          ensureCompleteRegionData(region, salesUnit)
        ),
      };
    });
  }, [card3Data, salesUnit]);

  useEffect(() => {
    if (data.length > 0) {
      setExpandedZones(data.map((row) => row.Zone));
      setExpandedRegions(
        data.flatMap((row) =>
          row.Regions.map((region) => `${row.Zone}-${region.Name}`)
        )
      );
    }
  }, [data]);

  const handleDownload = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData: any[] = [];

    const headers = [
      "Zone",
      ...(showRegion && !showZoneData ? ["Region"] : []),
      ...(showHQ && !showZoneData ? ["HQ"] : []),
      "Metric",
      ...(viewMode === "month" ? selectedMonths : Quarters),
      "Grand Total",
      "Balance",
    ];
    worksheetData.push(headers);

    data.forEach((row) => {
      if (showZoneData) {
        const baseRow = [row.Zone];
        worksheetData.push([
          ...baseRow,
          "LYTD",
          ...(viewMode === "month"
            ? selectedMonths.map((month) => row.Zone_Months[month]?.LYTD ?? 0)
            : Quarters.map((quarter) => row.Zone_Quarters[quarter]?.LYTD ?? 0)),
          row.Zone_GrandTotal.LYTD,
          row.Zone_Balance,
        ]);
        worksheetData.push([
          ...baseRow,
          "CYTD",
          ...(viewMode === "month"
            ? selectedMonths.map((month) => row.Zone_Months[month]?.CYTD ?? 0)
            : Quarters.map((quarter) => row.Zone_Quarters[quarter]?.CYTD ?? 0)),
          row.Zone_GrandTotal.CYTD,
          row.Zone_Balance,
        ]);
        worksheetData.push([
          ...baseRow,
          "Growth",
          ...(viewMode === "month"
            ? selectedMonths.map((month) => row.Zone_Months[month]?.Growth ?? 0)
            : Quarters.map(
                (quarter) => row.Zone_Quarters[quarter]?.Growth ?? 0
              )),
          row.Zone_GrandTotal.Growth,
          row.Zone_Balance,
        ]);
        worksheetData.push([
          ...baseRow,
          "Target",
          ...(viewMode === "month"
            ? selectedMonths.map((month) => row.Zone_Months[month]?.Target ?? 0)
            : Quarters.map(
                (quarter) => row.Zone_Quarters[quarter]?.Target ?? 0
              )),
          row.Zone_GrandTotal.Target,
          row.Zone_Balance,
        ]);
        worksheetData.push([
          ...baseRow,
          "%Ach",
          ...(viewMode === "month"
            ? selectedMonths.map(
                (month) => row.Zone_Months[month]?.Per_Ach ?? 0
              )
            : Quarters.map(
                (quarter) => row.Zone_Quarters[quarter]?.Per_Ach ?? 0
              )),
          row.Zone_GrandTotal.Per_Ach,
          row.Zone_Balance,
        ]);
      } else {
        row.Regions.forEach((region) => {
          if (showHQ) {
            region.HQs.forEach((hq) => {
              const baseRow = [
                row.Zone,
                ...(showRegion ? [region.Name] : []),
                ...(showHQ ? [hq.Name] : []),
              ];
              worksheetData.push([
                ...baseRow,
                "LYTD",
                ...(viewMode === "month"
                  ? selectedMonths.map((month) => hq.Months[month]?.LYTD ?? 0)
                  : Quarters.map((quarter) => hq.Quarters[quarter]?.LYTD ?? 0)),
                hq.GrandTotal.LYTD,
                hq.Balance,
              ]);
              worksheetData.push([
                ...baseRow,
                "CYTD",
                ...(viewMode === "month"
                  ? selectedMonths.map((month) => hq.Months[month]?.CYTD ?? 0)
                  : Quarters.map((quarter) => hq.Quarters[quarter]?.CYTD ?? 0)),
                hq.GrandTotal.CYTD,
                hq.Balance,
              ]);
              worksheetData.push([
                ...baseRow,
                "Growth",
                ...(viewMode === "month"
                  ? selectedMonths.map((month) => hq.Months[month]?.Growth ?? 0)
                  : Quarters.map(
                      (quarter) => hq.Quarters[quarter]?.Growth ?? 0
                    )),
                hq.GrandTotal.Growth,
                hq.Balance,
              ]);
              worksheetData.push([
                ...baseRow,
                "Target",
                ...(viewMode === "month"
                  ? selectedMonths.map((month) => hq.Months[month]?.Target ?? 0)
                  : Quarters.map(
                      (quarter) => hq.Quarters[quarter]?.Target ?? 0
                    )),
                hq.GrandTotal.Target,
                hq.Balance,
              ]);
              worksheetData.push([
                ...baseRow,
                "%Ach",
                ...(viewMode === "month"
                  ? selectedMonths.map(
                      (month) => hq.Months[month]?.Per_Ach ?? 0
                    )
                  : Quarters.map(
                      (quarter) => hq.Quarters[quarter]?.Per_Ach ?? 0
                    )),
                hq.GrandTotal.Per_Ach,
                hq.Balance,
              ]);
            });
          } else {
            const baseRow = [row.Zone, ...(showRegion ? [region.Name] : [])];
            worksheetData.push([
              ...baseRow,
              "LYTD",
              ...(viewMode === "month"
                ? selectedMonths.map(
                    (month) => region.Region_Months[month]?.LYTD ?? 0
                  )
                : Quarters.map(
                    (quarter) => region.Region_Quarters[quarter]?.LYTD ?? 0
                  )),
              region.Region_GrandTotal.LYTD,
              region.Region_Balance,
            ]);
            worksheetData.push([
              ...baseRow,
              "CYTD",
              ...(viewMode === "month"
                ? selectedMonths.map(
                    (month) => region.Region_Months[month]?.CYTD ?? 0
                  )
                : Quarters.map(
                    (quarter) => region.Region_Quarters[quarter]?.CYTD ?? 0
                  )),
              region.Region_GrandTotal.CYTD,
              region.Region_Balance,
            ]);
            worksheetData.push([
              ...baseRow,
              "Growth",
              ...(viewMode === "month"
                ? selectedMonths.map(
                    (month) => region.Region_Months[month]?.Growth ?? 0
                  )
                : Quarters.map(
                    (quarter) => region.Region_Quarters[quarter]?.Growth ?? 0
                  )),
              region.Region_GrandTotal.Growth,
              region.Region_Balance,
            ]);
            worksheetData.push([
              ...baseRow,
              "Target",
              ...(viewMode === "month"
                ? selectedMonths.map(
                    (month) => region.Region_Months[month]?.Target ?? 0
                  )
                : Quarters.map(
                    (quarter) => region.Region_Quarters[quarter]?.Target ?? 0
                  )),
              region.Region_GrandTotal.Target,
              region.Region_Balance,
            ]);
            worksheetData.push([
              ...baseRow,
              "%Ach",
              ...(viewMode === "month"
                ? selectedMonths.map(
                    (month) => region.Region_Months[month]?.Per_Ach ?? 0
                  )
                : Quarters.map(
                    (quarter) => region.Region_Quarters[quarter]?.Per_Ach ?? 0
                  )),
              region.Region_GrandTotal.Per_Ach,
              region.Region_Balance,
            ]);
          }
        });
      }
    });

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Performance Data");

    const csvString = XLSX.utils.sheet_to_csv(worksheet);
    const base64Csv = btoa(unescape(encodeURIComponent(csvString)));
    const fileName = `SalesByZone_Data_Report.csv`;

    const isReactNativeWebView = !!window.ReactNativeWebView;
    const isInIframe = window.self !== window.top;

    if (isReactNativeWebView) {
      const message = {
        type: "DOWNLOAD_CSV",
        csv: base64Csv,
        fileName,
        mimeType: "text/csv",
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    } else if (isInIframe) {
      const message = {
        type: "DOWNLOAD_CSV",
        csv: base64Csv,
        fileName: "SalesByZone_Data_Report.csv",
        mimeType: "text/csv",
      };
        const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "SalesTrend");
      XLSX.writeFile(workbook, "SalesByZone_Data_Report.xlsx");

      // ✅ Notify parent dynamically
      window.parent.postMessage(JSON.stringify(message), "https://bsvdashprod.mankindpharma.in");
      // window.parent.postMessage(
      //   JSON.stringify(message),
      //   "https://bsvdashprod.mankindpharma.in"
      // );
    } else {
      XLSX.writeFile(workbook, fileName.replace(".csv", ".xlsx"));
      alert("File downloaded successfully!");
    }
  };
  const handleApplyMonths = (newSelectedMonths: string[]) => {
    setSelectedMonths(newSelectedMonths);
  };

  const handleZoneHeaderClick = () => {
    setShowZoneData((prev) => !prev);
    if (!showZoneData) {
      setShowRegion(false);
      setShowHQ(false);
    } else {
      setShowRegion(true);
      setShowHQ(true);
    }
  };

  const handleRegionHeaderClick = () => {
    setShowHQ((prev) => !prev);
  };

  const handleViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setViewMode(event.target.value as "month" | "quarter");
    setTitle(
      viewMode !== "quarter" ? "Quarterly Performance" : "Monthly Performance"
    );
  };

  const displayedHeaders = viewMode === "month" ? selectedMonths : Quarters;

  if (loading || !kpiData ) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="monthly-card">
      <Box className="header">
        <Box className="title-container">
          <Typography className="title">
            {title} ({salesUnit || "Lacs"})
          </Typography>
        </Box>

        <Box className="controls">
          <FormControl className="view-mode-control">
            <RadioGroup
              value={viewMode}
              onChange={handleViewChange}
              style={{ flexDirection: "column" }}
            >
              <FormControlLabel
                className="view-mode-control-radio"
                value="quarter"
                control={<Radio className="custom-radio" />}
                label={
                  <Typography className="view-mode-label">Quarterly</Typography>
                }
              />
              <FormControlLabel
                className="view-mode-control-radio"
                value="month"
                control={<Radio className="custom-radio" />}
                label={
                  <Typography className="view-mode-label">Monthly</Typography>
                }
              />
            </RadioGroup>
          </FormControl>
          {viewMode === "month" && (
            <MonthSelectorDropdown
              months={allMonths}
              selectedMonths={selectedMonths}
              onApply={handleApplyMonths}
              label="Select Months"
            />
          )}
          <Button
            variant="contained"
            className="download-button"
            onClick={handleDownload}
          >
            Download MIS
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} className="table-container">
        <Table className="table" size="small">
          <TableHead>
            <TableRow className="header-row">
              <TableCell
                className="sticky-header zone-header cursor-pointer"
                rowSpan={2}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center" }}
                  onClick={handleZoneHeaderClick}
                >
                  <Typography className="header-text">
                    <b>Zone </b>
                  </Typography>
                  <Typography className="header-text ">
                    {showZoneData ? "−" : "+"}
                  </Typography>
                </Box>
              </TableCell>
              {showRegion && (
                <TableCell
                  sx={{ cursor: "pointer" }}
                  className="sticky-header region-header cursor-pointer"
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center" }}
                    onClick={handleRegionHeaderClick}
                  >
                    <Typography className="header-text">
                      <b>Region </b>
                    </Typography>
                    <Typography className="header-text ">
                      {showHQ ? "+" : "-"}
                    </Typography>
                  </Box>
                </TableCell>
              )}

              {showHQ && (
                <TableCell
                  className={`sticky-header hq-header ${
                    showRegion ? "with-region" : ""
                  }`}
                >
                  <b>HQ</b>
                </TableCell>
              )}
              <TableCell
                className={`sticky-header label-column ${
                  showRegion && showHQ
                    ? "both-visible"
                    : showRegion || showHQ
                    ? "one-visible"
                    : ""
                }`}
              ></TableCell>
              {displayedHeaders.map((header) => (
                <TableCell key={header} className="month-header" align="center">
                  <b>{header}</b>
                </TableCell>
              ))}
              <TableCell className="grand-total-header">
                <b>Grand Total</b>
              </TableCell>
              <TableCell className="grand-total-header">
                <b>Balance</b>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row, zoneIndex) => (
              <React.Fragment key={`zone-${zoneIndex}`}>
                {expandedZones.includes(row.Zone) && (
                  <>
                    {showZoneData ? (
                      <React.Fragment>
                        <TableRow>
                          <TableCell
                            rowSpan={5}
                            className="sticky-body zone-cell"
                          >
                            <Typography className="cell-text">
                              <b>{row.Zone || "Null"}</b>
                            </Typography>
                          </TableCell>
                          <TableCell className="sticky-body label-cell">
                            <b>LYTD</b>
                          </TableCell>
                          {displayedHeaders.map((header) => (
                            <TableCell
                              key={header}
                              className="month-cell"
                              align="center"
                            >
                              {viewMode === "month"
                                ? row.Zone_Months?.[header]?.LYTD ?? 0
                                : row.Zone_Quarters?.[header]?.LYTD ?? 0}
                            </TableCell>
                          ))}
                          <TableCell className="grand-total-cell">
                            {row.Zone_GrandTotal?.LYTD ?? 0}
                          </TableCell>
                          <TableCell rowSpan={5} className="balance-cell">
                            {row.Zone_Balance ?? 0}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="sticky-body label-cell">
                            <b>Target</b>
                          </TableCell>
                          {displayedHeaders.map((header) => (
                            <TableCell
                              key={header}
                              className="month-cell"
                              align="center"
                            >
                              {viewMode === "month"
                                ? row.Zone_Months?.[header]?.Target ?? 0
                                : row.Zone_Quarters?.[header]?.Target ?? 0}
                            </TableCell>
                          ))}
                          <TableCell className="grand-total-cell">
                            {row.Zone_GrandTotal?.Target ?? 0}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="sticky-body label-cell">
                            <b>CYTD</b>
                          </TableCell>
                          {displayedHeaders.map((header) => (
                            <TableCell
                              key={header}
                              className="month-cell"
                              align="center"
                            >
                              {viewMode === "month"
                                ? row.Zone_Months?.[header]?.CYTD ?? 0
                                : row.Zone_Quarters?.[header]?.CYTD ?? 0}
                            </TableCell>
                          ))}
                          <TableCell className="grand-total-cell">
                            {row.Zone_GrandTotal?.CYTD ?? 0}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="sticky-body label-cell">
                            <b>%Ach</b>
                          </TableCell>
                          {displayedHeaders.map((header) => {
                            const perAchValue =
                              viewMode === "month"
                                ? row.Zone_Months?.[header]?.Per_Ach ?? 0
                                : row.Zone_Quarters?.[header]?.Per_Ach ?? 0;
                            const numericValue = Number(perAchValue);
                            const isPositive = numericValue > 0;
                            const isNegative = numericValue < 0;
                            const displayValue =
                              numericValue === 0
                                ? "0"
                                : `${numericValue.toFixed(2)}%`;
                            return (
                              <TableCell
                                key={header}
                                className={`month-cell growth-cell ${
                                  isNegative ? "negative" : "positive"
                                }`}
                                align="center"
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {isPositive ? (
                                    <img
                                      src={ArrowUpwardIcon}
                                      alt="Up Arrow"
                                      style={{
                                        width: 16,
                                        height: 16,
                                        marginRight: 4,
                                      }}
                                    />
                                  ) : isNegative ? (
                                    <img
                                      src={ArrowDownwardIcon}
                                      alt="Down Arrow"
                                      style={{
                                        width: 16,
                                        height: 16,
                                        marginRight: 4,
                                      }}
                                    />
                                  ) : null}
                                  <Typography
                                    sx={{
                                      color: isNegative
                                        ? "red"
                                        : isPositive
                                        ? "green"
                                        : "black",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {displayValue}
                                  </Typography>
                                </Box>
                              </TableCell>
                            );
                          })}
                          <TableCell className="grand-total-cell">
                            {(() => {
                              const perAchValue =
                                row.Zone_GrandTotal?.Per_Ach ?? 0;
                              const numericValue = Number(perAchValue);
                              const isPositive = numericValue > 0;
                              const isNegative = numericValue < 0;
                              const displayValue =
                                numericValue === 0
                                  ? "0"
                                  : `${numericValue.toFixed(2)}%`;
                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {isPositive ? (
                                    <img
                                      src={ArrowUpwardIcon}
                                      alt="Up Arrow"
                                      style={{
                                        width: 16,
                                        height: 16,
                                        marginRight: 4,
                                      }}
                                    />
                                  ) : isNegative ? (
                                    <img
                                      src={ArrowDownwardIcon}
                                      alt="Down Arrow"
                                      style={{
                                        width: 16,
                                        height: 16,
                                        marginRight: 4,
                                      }}
                                    />
                                  ) : null}
                                  <Typography
                                    sx={{
                                      color: isNegative
                                        ? "red"
                                        : isPositive
                                        ? "green"
                                        : "black",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {displayValue}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="sticky-body label-cell">
                            <b>Grw</b>
                          </TableCell>
                          {displayedHeaders.map((header) => {
                            const growthValue =
                              viewMode === "month"
                                ? row.Zone_Months?.[header]?.Growth ?? 0
                                : row.Zone_Quarters?.[header]?.Growth ?? 0;
                            const numericValue = Number(growthValue);
                            const isPositive = numericValue > 0;
                            const isNegative = numericValue < 0;
                            const displayValue =
                              numericValue === 0
                                ? "0"
                                : `${numericValue.toFixed(2)}%`;
                            return (
                              <TableCell
                                key={header}
                                className={`month-cell growth-cell ${
                                  isNegative ? "negative" : "positive"
                                }`}
                                align="center"
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {isPositive ? (
                                    <img
                                      src={ArrowUpwardIcon}
                                      alt="Up Arrow"
                                      style={{
                                        width: 16,
                                        height: 16,
                                        marginRight: 4,
                                      }}
                                    />
                                  ) : isNegative ? (
                                    <img
                                      src={ArrowDownwardIcon}
                                      alt="Down Arrow"
                                      style={{
                                        width: 16,
                                        height: 16,
                                        marginRight: 4,
                                      }}
                                    />
                                  ) : null}
                                  <Typography
                                    sx={{
                                      color: isNegative
                                        ? "red"
                                        : isPositive
                                        ? "green"
                                        : "black",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {displayValue}
                                  </Typography>
                                </Box>
                              </TableCell>
                            );
                          })}
                          <TableCell className="grand-total-cell">
                            {(() => {
                              const growthValue =
                                row.Zone_GrandTotal?.Growth ?? 0;
                              const numericValue = Number(growthValue);
                              const isPositive = numericValue > 0;
                              const isNegative = numericValue < 0;
                              const displayValue =
                                numericValue === 0
                                  ? "0"
                                  : `${numericValue.toFixed(2)}%`;
                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {isPositive ? (
                                    <img
                                      src={ArrowUpwardIcon}
                                      alt="Up Arrow"
                                      style={{
                                        width: 16,
                                        height: 16,
                                        marginRight: 4,
                                      }}
                                    />
                                  ) : isNegative ? (
                                    <img
                                      src={ArrowDownwardIcon}
                                      alt="Down Arrow"
                                      style={{
                                        width: 16,
                                        height: 16,
                                        marginRight: 4,
                                      }}
                                    />
                                  ) : null}
                                  <Typography
                                    sx={{
                                      color: isNegative
                                        ? "red"
                                        : isPositive
                                        ? "green"
                                        : "black",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {displayValue}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ) : (
                      row.Regions.map((region, regionIndex) => (
                        <React.Fragment
                          key={`region-${zoneIndex}-${regionIndex}`}
                        >
                          {expandedRegions.includes(
                            `${row.Zone}-${region.Name}`
                          ) &&
                            (showHQ ? (
                              region.HQs.map((hq, hqIndex) => (
                                <React.Fragment
                                  key={`hq-${zoneIndex}-${regionIndex}-${hqIndex}`}
                                >
                                  <TableRow>
                                    {hqIndex === 0 && regionIndex === 0 && (
                                      <TableCell
                                        rowSpan={row.Regions.reduce(
                                          (sum, r) =>
                                            sum +
                                            (expandedRegions.includes(
                                              `${row.Zone}-${r.Name}`
                                            )
                                              ? r.HQs.length * 5
                                              : 0),
                                          0
                                        )}
                                        className="sticky-body zone-cell"
                                      >
                                        <Typography className="cell-text">
                                          <b>{row.Zone || "Null"}</b>
                                        </Typography>
                                      </TableCell>
                                    )}
                                    {hqIndex === 0 && showRegion && (
                                      <TableCell
                                        rowSpan={region.HQs.length * 5}
                                        className="sticky-body region-cell"
                                      >
                                        <Typography className="cell-text">
                                          <b>{region.Name || "Null"}</b>
                                        </Typography>
                                      </TableCell>
                                    )}
                                    <TableCell
                                      rowSpan={5}
                                      className={`sticky-body hq-cell ${
                                        showRegion ? "with-region" : ""
                                      }`}
                                    >
                                      <Typography className="cell-text">
                                        <b>{hq.Name || "Null"}</b>
                                      </Typography>
                                    </TableCell>
                                    <TableCell
                                      className={`sticky-body label-cell ${
                                        showRegion && showHQ
                                          ? "both-visible"
                                          : showRegion || showHQ
                                          ? "one-visible"
                                          : ""
                                      }`}
                                    >
                                      <b>LYTD</b>
                                    </TableCell>
                                    {displayedHeaders.map((header) => (
                                      <TableCell
                                        key={header}
                                        className="month-cell"
                                        align="center"
                                      >
                                        {viewMode === "month"
                                          ? hq.Months?.[header]?.LYTD ?? 0
                                          : hq.Quarters?.[header]?.LYTD ?? 0}
                                      </TableCell>
                                    ))}
                                    <TableCell className="grand-total-cell">
                                      {hq.GrandTotal?.LYTD ?? 0}
                                    </TableCell>
                                    <TableCell
                                      rowSpan={5}
                                      className="balance-cell"
                                    >
                                      {hq.Balance ?? 0}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell
                                      className={`sticky-body label-cell ${
                                        showRegion && showHQ
                                          ? "both-visible"
                                          : showRegion || showHQ
                                          ? "one-visible"
                                          : ""
                                      }`}
                                    >
                                      <b>Target</b>
                                    </TableCell>
                                    {displayedHeaders.map((header) => (
                                      <TableCell
                                        key={header}
                                        className="month-cell"
                                        align="center"
                                      >
                                        {viewMode === "month"
                                          ? hq.Months?.[header]?.Target ?? 0
                                          : hq.Quarters?.[header]?.Target ?? 0}
                                      </TableCell>
                                    ))}
                                    <TableCell className="grand-total-cell">
                                      {hq.GrandTotal?.Target ?? 0}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell
                                      className={`sticky-body label-cell ${
                                        showRegion && showHQ
                                          ? "both-visible"
                                          : showRegion || showHQ
                                          ? "one-visible"
                                          : ""
                                      }`}
                                    >
                                      <b>CYTD</b>
                                    </TableCell>
                                    {displayedHeaders.map((header) => (
                                      <TableCell
                                        key={header}
                                        className="month-cell"
                                        align="center"
                                      >
                                        {viewMode === "month"
                                          ? hq.Months?.[header]?.CYTD ?? 0
                                          : hq.Quarters?.[header]?.CYTD ?? 0}
                                      </TableCell>
                                    ))}
                                    <TableCell className="grand-total-cell">
                                      {hq.GrandTotal?.CYTD ?? 0}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell
                                      className={`sticky-body label-cell ${
                                        showRegion && showHQ
                                          ? "both-visible"
                                          : showRegion || showHQ
                                          ? "one-visible"
                                          : ""
                                      }`}
                                    >
                                      <b>%Ach</b>
                                    </TableCell>
                                    {displayedHeaders.map((header) => {
                                      const perAchValue =
                                        viewMode === "month"
                                          ? hq.Months?.[header]?.Per_Ach ?? 0
                                          : hq.Quarters?.[header]?.Per_Ach ?? 0;
                                      const numericValue = Number(perAchValue);
                                      const isPositive = numericValue > 0;
                                      const isNegative = numericValue < 0;
                                      const displayValue =
                                        numericValue === 0
                                          ? "0"
                                          : `${numericValue.toFixed(2)}%`;
                                      return (
                                        <TableCell
                                          key={header}
                                          className={`month-cell growth-cell ${
                                            isNegative ? "negative" : "positive"
                                          }`}
                                          align="center"
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            {isPositive ? (
                                              <img
                                                src={ArrowUpwardIcon}
                                                alt="Up Arrow"
                                                style={{
                                                  width: 16,
                                                  height: 16,
                                                  marginRight: 4,
                                                }}
                                              />
                                            ) : isNegative ? (
                                              <img
                                                src={ArrowDownwardIcon}
                                                alt="Down Arrow"
                                                style={{
                                                  width: 16,
                                                  height: 16,
                                                  marginRight: 4,
                                                }}
                                              />
                                            ) : null}
                                            <Typography
                                              sx={{
                                                color: isNegative
                                                  ? "red"
                                                  : isPositive
                                                  ? "green"
                                                  : "black",
                                                fontSize: "14px",
                                              }}
                                            >
                                              {displayValue}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell className="grand-total-cell">
                                      {(() => {
                                        const perAchValue =
                                          hq.GrandTotal?.Per_Ach ?? 0;
                                        const numericValue =
                                          Number(perAchValue);
                                        const isPositive = numericValue > 0;
                                        const isNegative = numericValue < 0;
                                        const displayValue =
                                          numericValue === 0
                                            ? "0"
                                            : `${numericValue.toFixed(2)}%`;
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            {isPositive ? (
                                              <img
                                                src={ArrowUpwardIcon}
                                                alt="Up Arrow"
                                                style={{
                                                  width: 16,
                                                  height: 16,
                                                  marginRight: 4,
                                                }}
                                              />
                                            ) : isNegative ? (
                                              <img
                                                src={ArrowDownwardIcon}
                                                alt="Down Arrow"
                                                style={{
                                                  width: 16,
                                                  height: 16,
                                                  marginRight: 4,
                                                }}
                                              />
                                            ) : null}
                                            <Typography
                                              sx={{
                                                color: isNegative
                                                  ? "red"
                                                  : isPositive
                                                  ? "green"
                                                  : "black",
                                                fontSize: "14px",
                                              }}
                                            >
                                              {displayValue}
                                            </Typography>
                                          </Box>
                                        );
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell
                                      className={`sticky-body label-cell ${
                                        showRegion && showHQ
                                          ? "both-visible"
                                          : showRegion || showHQ
                                          ? "one-visible"
                                          : ""
                                      }`}
                                    >
                                      <b>Grw</b>
                                    </TableCell>
                                    {displayedHeaders.map((header) => {
                                      const growthValue =
                                        viewMode === "month"
                                          ? hq.Months?.[header]?.Growth ?? 0
                                          : hq.Quarters?.[header]?.Growth ?? 0;
                                      const numericValue = Number(growthValue);
                                      const isPositive = numericValue > 0;
                                      const isNegative = numericValue < 0;
                                      const displayValue =
                                        numericValue === 0
                                          ? "0"
                                          : `${numericValue.toFixed(2)}%`;
                                      return (
                                        <TableCell
                                          key={header}
                                          className={`month-cell growth-cell ${
                                            isNegative ? "negative" : "positive"
                                          }`}
                                          align="center"
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            {isPositive ? (
                                              <img
                                                src={ArrowUpwardIcon}
                                                alt="Up Arrow"
                                                style={{
                                                  width: 16,
                                                  height: 16,
                                                  marginRight: 4,
                                                }}
                                              />
                                            ) : isNegative ? (
                                              <img
                                                src={ArrowDownwardIcon}
                                                alt="Down Arrow"
                                                style={{
                                                  width: 16,
                                                  height: 16,
                                                  marginRight: 4,
                                                }}
                                              />
                                            ) : null}
                                            <Typography
                                              sx={{
                                                color: isNegative
                                                  ? "red"
                                                  : isPositive
                                                  ? "green"
                                                  : "black",
                                                fontSize: "14px",
                                              }}
                                            >
                                              {displayValue}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell className="grand-total-cell">
                                      {(() => {
                                        const growthValue =
                                          hq.GrandTotal?.Growth ?? 0;
                                        const numericValue =
                                          Number(growthValue);
                                        const isPositive = numericValue > 0;
                                        const isNegative = numericValue < 0;
                                        const displayValue =
                                          numericValue === 0
                                            ? "0"
                                            : `${numericValue.toFixed(2)}%`;
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            {isPositive ? (
                                              <img
                                                src={ArrowUpwardIcon}
                                                alt="Up Arrow"
                                                style={{
                                                  width: 16,
                                                  height: 16,
                                                  marginRight: 4,
                                                }}
                                              />
                                            ) : isNegative ? (
                                              <img
                                                src={ArrowDownwardIcon}
                                                alt="Down Arrow"
                                                style={{
                                                  width: 16,
                                                  height: 16,
                                                  marginRight: 4,
                                                }}
                                              />
                                            ) : null}
                                            <Typography
                                              sx={{
                                                color: isNegative
                                                  ? "red"
                                                  : isPositive
                                                  ? "green"
                                                  : "inherit",
                                                fontSize: "14px",
                                              }}
                                            >
                                              {displayValue}
                                            </Typography>
                                          </Box>
                                        );
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              ))
                            ) : (
                              <React.Fragment>
                                <TableRow>
                                  {regionIndex === 0 && (
                                    <TableCell
                                      rowSpan={row.Regions.length * 5}
                                      className="sticky-body zone-cell"
                                    >
                                      <Typography className="cell-text">
                                        <b>{row.Zone || "Null"}</b>
                                      </Typography>
                                    </TableCell>
                                  )}
                                  {showRegion && (
                                    <TableCell
                                      rowSpan={5}
                                      className="sticky-body region-cell"
                                    >
                                      <Typography className="cell-text">
                                        <b>{region.Name || "Null"}</b>
                                      </Typography>
                                    </TableCell>
                                  )}
                                  <TableCell
                                    className={`sticky-body label-cell ${
                                      showRegion ? "one-visible" : ""
                                    }`}
                                  >
                                    <b>LYTD</b>
                                  </TableCell>
                                  {displayedHeaders.map((header) => (
                                    <TableCell
                                      key={header}
                                      className="month-cell"
                                      align="center"
                                    >
                                      {viewMode === "month"
                                        ? region.Region_Months?.[header]
                                            ?.LYTD ?? 0
                                        : region.Region_Quarters?.[header]
                                            ?.LYTD ?? 0}
                                    </TableCell>
                                  ))}
                                  <TableCell className="grand-total-cell">
                                    {region.Region_GrandTotal?.LYTD ?? 0}
                                  </TableCell>
                                  <TableCell
                                    rowSpan={5}
                                    className="balance-cell"
                                  >
                                    {region.Region_Balance ?? 0}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    className={`sticky-body label-cell ${
                                      showRegion ? "one-visible" : ""
                                    }`}
                                  >
                                    <b>Target</b>
                                  </TableCell>
                                  {displayedHeaders.map((header) => (
                                    <TableCell
                                      key={header}
                                      className="month-cell"
                                      align="center"
                                    >
                                      {viewMode === "month"
                                        ? region.Region_Months?.[header]
                                            ?.Target ?? 0
                                        : region.Region_Quarters?.[header]
                                            ?.Target ?? 0}
                                    </TableCell>
                                  ))}
                                  <TableCell className="grand-total-cell">
                                    {region.Region_GrandTotal?.Target ?? 0}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    className={`sticky-body label-cell ${
                                      showRegion ? "one-visible" : ""
                                    }`}
                                  >
                                    <b>CYTD</b>
                                  </TableCell>
                                  {displayedHeaders.map((header) => (
                                    <TableCell
                                      key={header}
                                      className="month-cell"
                                      align="center"
                                    >
                                      {viewMode === "month"
                                        ? region.Region_Months?.[header]
                                            ?.CYTD ?? 0
                                        : region.Region_Quarters?.[header]
                                            ?.CYTD ?? 0}
                                    </TableCell>
                                  ))}
                                  <TableCell className="grand-total-cell">
                                    {region.Region_GrandTotal?.CYTD ?? 0}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    className={`sticky-body label-cell ${
                                      showRegion ? "one-visible" : ""
                                    }`}
                                  >
                                    <b>%Ach</b>
                                  </TableCell>
                                  {displayedHeaders.map((header) => {
                                    const perAchValue =
                                      viewMode === "month"
                                        ? region.Region_Months?.[header]
                                            ?.Per_Ach ?? 0
                                        : region.Region_Quarters?.[header]
                                            ?.Per_Ach ?? 0;
                                    const numericValue = Number(perAchValue);
                                    const isPositive = numericValue > 0;
                                    const isNegative = numericValue < 0;
                                    const displayValue =
                                      numericValue === 0
                                        ? "0"
                                        : `${numericValue.toFixed(2)}%`;
                                    return (
                                      <TableCell
                                        className={`month-cell growth-cell ${
                                          isNegative ? "negative" : "positive"
                                        }`}
                                        align="center"
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {isPositive ? (
                                            <img
                                              src={ArrowUpwardIcon}
                                              alt="Up Arrow"
                                              style={{
                                                width: 16,
                                                height: 16,
                                                marginRight: 4,
                                              }}
                                            />
                                          ) : isNegative ? (
                                            <img
                                              src={ArrowDownwardIcon}
                                              alt="Down Arrow"
                                              style={{
                                                width: 16,
                                                height: 16,
                                                marginRight: 4,
                                              }}
                                            />
                                          ) : null}
                                          <Typography
                                            sx={{
                                              color: isNegative
                                                ? "red"
                                                : isPositive
                                                ? "green"
                                                : "black",
                                              fontSize: "14px",
                                            }}
                                          >
                                            {displayValue}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell className="grand-total-cell">
                                    {(() => {
                                      const perAchValue =
                                        region.Region_GrandTotal?.Per_Ach ?? 0;
                                      const numericValue = Number(perAchValue);
                                      const isPositive = numericValue > 0;
                                      const isNegative = numericValue < 0;
                                      const displayValue =
                                        numericValue === 0
                                          ? "0"
                                          : `${numericValue.toFixed(2)}%`;
                                      return (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {isPositive ? (
                                            <img
                                              src={ArrowUpwardIcon}
                                              alt="Up Arrow"
                                              style={{
                                                width: 16,
                                                height: 16,
                                                marginRight: 4,
                                              }}
                                            />
                                          ) : isNegative ? (
                                            <img
                                              src={ArrowDownwardIcon}
                                              alt="Down Arrow"
                                              style={{
                                                width: 16,
                                                height: 16,
                                                marginRight: 4,
                                              }}
                                            />
                                          ) : null}
                                          <Typography
                                            sx={{
                                              color: isNegative
                                                ? "red"
                                                : isPositive
                                                ? "green"
                                                : "black",
                                              fontSize: "14px",
                                            }}
                                          >
                                            {displayValue}
                                          </Typography>
                                        </Box>
                                      );
                                    })()}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    className={`sticky-body label-cell ${
                                      showRegion ? "one-visible" : ""
                                    }`}
                                  >
                                    <b>Grw</b>
                                  </TableCell>
                                  {displayedHeaders.map((header) => {
                                    const growthValue =
                                      viewMode === "month"
                                        ? region.Region_Months?.[header]
                                            ?.Growth ?? 0
                                        : region.Region_Quarters?.[header]
                                            ?.Growth ?? 0;
                                    const numericValue = Number(growthValue);
                                    const isPositive = numericValue > 0;
                                    const isNegative = numericValue < 0;
                                    const displayValue =
                                      numericValue === 0
                                        ? "0"
                                        : `${numericValue.toFixed(2)}%`;
                                    return (
                                      <TableCell
                                        className={`month-cell growth-cell ${
                                          isNegative ? "negative" : "positive"
                                        }`}
                                        align="center"
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {isPositive ? (
                                            <img
                                              src={ArrowUpwardIcon}
                                              alt="Up Arrow"
                                              style={{
                                                width: 16,
                                                height: 16,
                                                marginRight: 4,
                                              }}
                                            />
                                          ) : isNegative ? (
                                            <img
                                              src={ArrowDownwardIcon}
                                              alt="Down Arrow"
                                              style={{
                                                width: 16,
                                                height: 16,
                                                marginRight: 4,
                                              }}
                                            />
                                          ) : null}
                                          <Typography
                                            sx={{
                                              color: isNegative
                                                ? "red"
                                                : isPositive
                                                ? "green"
                                                : "black",
                                              fontSize: "14px",
                                            }}
                                          >
                                            {displayValue}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell className="grand-total-cell">
                                    {(() => {
                                      const growthValue =
                                        region.Region_GrandTotal?.Growth ?? 0;
                                      const numericValue = Number(growthValue);
                                      const isPositive = numericValue > 0;
                                      const isNegative = numericValue < 0;
                                      const displayValue =
                                        numericValue === 0
                                          ? "0"
                                          : `${numericValue.toFixed(2)}%`;
                                      return (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {isPositive ? (
                                            <img
                                              src={ArrowUpwardIcon}
                                              alt="Up Arrow"
                                              style={{
                                                width: 16,
                                                height: 16,
                                                marginRight: 4,
                                              }}
                                            />
                                          ) : isNegative ? (
                                            <img
                                              src={ArrowDownwardIcon}
                                              alt="Down Arrow"
                                              style={{
                                                width: 16,
                                                height: 16,
                                                marginRight: 4,
                                              }}
                                            />
                                          ) : null}
                                          <Typography
                                            sx={{
                                              color: isNegative
                                                ? "red"
                                                : isPositive
                                                ? "green"
                                                : "inherit",
                                            }}
                                          >
                                            {displayValue}
                                          </Typography>
                                        </Box>
                                      );
                                    })()}
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            ))}
                        </React.Fragment>
                      ))
                    )}
                  </>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FixedColumnTable;
